import { ExclamationCircleIcon, MapPinIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { useQueries } from '@tanstack/react-query';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { useState } from 'react';
import { NumericFormat as NumberFormat } from 'react-number-format';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';
import { getAddressDecode, type IGetAddressDecodeResData } from '@/api/misc';
import { patchProject } from '@/api/Sherpa/Project';
import ImageCircularAvatar from '@/components/UI/Avatar/ImageCircularAvatar';
import GoogleMap from '@/components/UI/GoogleMap';
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import { accountSelector } from '@/store';
import { getInitials } from '@/utils';
import { calculateProjectBudgetTotal, getCCLFLoan } from '@/utils/project';
import type { DashboardProjectByLocationInterface } from '../../../Widgets/ProjectLocationWidget';

interface IProjectGeoLocated extends DashboardProjectByLocationInterface {
  coordinates: { lat: number; lng: number };
}

const ProjectLocationMap = ({ projectList }: { projectList: DashboardProjectByLocationInterface[] }) => {
  const [selectedProject, setSelectedProject] = useState<IProjectGeoLocated>();
  const { data: currentClientAccount } = useSelector(accountSelector);

  const projectsWithoutGeoLocation = projectList.filter((p) => !p.location.lat && !p.location.lng);

  const projectsWithGeoLocation = projectList.filter((p) => p.location.lat && p.location.lng);

  const fetchProjectLocation = useQueries({
    queries: projectsWithoutGeoLocation?.map((d: DashboardProjectByLocationInterface) => {
      const fullAddress = `${d.address}, ${d.city}, ${d.state}, ${d.zipCode}`;
      return {
        queryKey: ['GET_PROJECT_WIDGET_GEO_CODE', d],
        queryFn: () => getAddressDecode(fullAddress),
        select: (resData: { data: IGetAddressDecodeResData }) => {
          const { lat, lng, place_id } = resData.data;

          patchProject({
            projectId: d.id,
            data: {
              location: {
                ...d.location,
                lat,
                lng,
                placeId: place_id,
              },
            },
          });

          return {
            ...d,
            location: {
              ...d.location,
              lat,
              lng,
              placeId: place_id,
            },
          };
        },
      };
    }),
  });

  const projectsGeoLocated: any[] = fetchProjectLocation?.map((d) => d.data).filter((df) => df);

  const allProjects = projectsWithGeoLocation.concat(projectsGeoLocated);

  return (
    <>
      {allProjects[0] && (
        <GoogleMap
          defaultCenter={{
            lat: allProjects[0].location.lat,
            lng: allProjects[0].location.lng,
          }}
          defaultZoom={10}
        >
          {allProjects?.map((project: any) => {
            return (
              <AdvancedMarker
                key={project.id}
                position={{
                  lat: project.location.lat,
                  lng: project.location.lng,
                }}
              >
                <div className='relative'>
                  {selectedProject && selectedProject?.id === project?.id && (
                    <div
                      onClick={() => setSelectedProject(undefined)}
                      className='bg-white border rounded absolute p-3 bottom-4 left-5 w-56'
                    >
                      <XCircleIcon className='h-4 w-4 absolute right-1 top-1 text-neutralColor-normal' />
                      <Link to={`/app/sherpa/projects/${selectedProject?.id}`}>
                        <p className='font-semibold text-primaryColor-normal underline'>{selectedProject?.name}</p>
                      </Link>
                      <p className='text-xs'>{selectedProject?.address}</p>
                      <p className='text-xs'>
                        {selectedProject?.location.city ?? selectedProject?.city},{' '}
                        {selectedProject?.location.state ?? selectedProject?.state},{' '}
                        {selectedProject?.location.postalCode ?? selectedProject?.postalCode}
                      </p>
                      {selectedProject?.lead && (
                        <div className='mt-2 flex gap-2 items-center'>
                          <ImageCircularAvatar
                            imageUrl={selectedProject?.lead.profileImageUrl}
                            fallbackText={getInitials(selectedProject?.lead.name)}
                          />
                          <div>
                            <p className='font-bold'>{selectedProject?.lead.name}</p>
                            <p className='text-xs'>Project Lead</p>
                          </div>
                        </div>
                      )}
                      {selectedProject?.sources?.length > 0 && (
                        <div className='mt-2 text-sm flex justify-between gap-2'>
                          <span>Project Cost</span>
                          <span className='font-semibold'>
                            <CurrencyFormatDisplay value={calculateProjectBudgetTotal(selectedProject?.sources)} prefix='$' />
                          </span>
                        </div>
                      )}

                      {selectedProject?.squareFeet && (
                        <div className='mt-2 text-sm flex justify-between gap-2'>
                          <span>Sq.Ft</span>
                          <span className='font-semibold'>
                            <NumberFormat value={selectedProject?.squareFeet} displayType='text' thousandSeparator={true} /> Sq.Ft
                          </span>
                        </div>
                      )}
                      {currentClientAccount.subdomain === 'chicagorebuild' && selectedProject?.sources?.length > 0 && (
                        <div className='mt-2 text-sm flex justify-between gap-2'>
                          <span>CCLF Loan</span>
                          <span className='font-semibold'>
                            <CurrencyFormatDisplay value={getCCLFLoan(selectedProject?.sources)} prefix='$' />
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  <MapPinIcon
                    onClick={() => setSelectedProject(project)}
                    className={selectedProject?.id === project?.id ? 'h-8 w-8 text-black' : 'h-8 w-8 text-red-500 '}
                  />
                </div>
              </AdvancedMarker>
            );
          })}
        </GoogleMap>
      )}
      {allProjects?.length === 0 && (
        <div className='w-full flex justify-center items-center h-screen/2'>
          <div>
            <div className='flex justify-center items-center gap-2 text-tertiaryColor-normal'>
              <ExclamationCircleIcon className='h-8 w-8' />
              <h1>Error</h1>
            </div>
            <p>Oops ! Something went wrong in the map. Please try again later.</p>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectLocationMap;
